export interface ApiErrorBody {
  readonly code: string;
  readonly message: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Readonly<Record<string, unknown>>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;

  readonly anonymous?: boolean;
  readonly signal?: AbortSignal;
}

type Fetcher = typeof fetch;

function resolveBase(baseUrl: string): string {
  const trimmed = baseUrl.replace(/\/$/, '');
  if (/^https?:\/\//i.test(trimmed)) {
    return `${trimmed}/`;
  }
  const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  return `${origin}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}/`;
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), resolveBase(baseUrl));
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function parseEnvelope(parsed: unknown, fallbackMessage: string): ApiErrorBody | null {
  if (typeof parsed !== 'object' || parsed === null || !('error' in parsed)) {
    return null;
  }
  const envelope = (parsed as { error: unknown }).error;
  if (typeof envelope !== 'object' || envelope === null) {
    return null;
  }
  const body = envelope as Partial<ApiErrorBody>;
  return {
    code: body.code ?? 'UNKNOWN',
    message: body.message ?? fallbackMessage,
    ...(body.details !== undefined ? { details: body.details } : {}),
  };
}

async function readError(response: Response): Promise<ApiErrorBody> {
  const fallback = response.statusText || 'Request failed';
  try {
    const parsed: unknown = await response.json();
    return parseEnvelope(parsed, fallback) ?? { code: 'UNKNOWN', message: fallback };
  } catch {

    return { code: 'UNKNOWN', message: fallback };
  }
}

export class ApiClient {
  private accessToken: string | null = null;
  private refreshInFlight: Promise<boolean> | null = null;
  private onAuthLost: (() => void) | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly fetcher: Fetcher = fetch.bind(globalThis),
  ) {}

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  hasAccessToken(): boolean {
    return this.accessToken !== null;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  onUnauthenticated(handler: () => void): void {
    this.onAuthLost = handler;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(path, options);
    if (response.status === 401 && options.anonymous !== true) {
      const refreshed = await this.refreshOnce();
      if (refreshed) {
        return this.unwrap<T>(await this.send(path, options));
      }
      this.onAuthLost?.();
    }
    return this.unwrap<T>(response);
  }

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (options.anonymous !== true && this.accessToken !== null) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return this.fetcher(buildUrl(this.baseUrl, path, options.query), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  }

  private async unwrap<T>(response: Response): Promise<T> {
    if (!response.ok) {
      throw new ApiError(response.status, await readError(response));
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  private async refreshOnce(): Promise<boolean> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const response = await this.send('auth/refresh', {
        method: 'POST',
        body: {},
        anonymous: true,
      });
      if (!response.ok) {
        this.accessToken = null;
        return false;
      }
      const body = (await response.json()) as { accessToken?: unknown };
      if (typeof body.accessToken !== 'string') {
        this.accessToken = null;
        return false;
      }
      this.accessToken = body.accessToken;
      return true;
    } catch {
      this.accessToken = null;
      return false;
    }
  }
}
